import React, { useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Chip, TextField, Alert,
    CircularProgress, Divider, Paper, Stack,
} from '@mui/material';
import {
    MergeType, Person, Email, Phone, PhoneAndroid,
    Notes, CheckCircle, Warning,
} from '@mui/icons-material';
import { Customer, DuplicateCandidate, customerMergeService } from '../../services/customerMergeService';

// Fields users can pick between
type MergeableField = 'first_name' | 'last_name' | 'email' | 'phone' | 'alternative_phone' | 'note';

const FIELD_META: { key: MergeableField; label: string; icon: React.ReactNode }[] = [
    { key: 'first_name',        label: 'First Name',        icon: <Person fontSize="small" /> },
    { key: 'last_name',         label: 'Last Name',         icon: <Person fontSize="small" /> },
    { key: 'email',             label: 'Email',             icon: <Email fontSize="small" /> },
    { key: 'phone',             label: 'Phone',             icon: <Phone fontSize="small" /> },
    { key: 'alternative_phone', label: 'Alt. Phone',        icon: <PhoneAndroid fontSize="small" /> },
    { key: 'note',              label: 'Notes',             icon: <Notes fontSize="small" /> },
];

interface Props {
    open: boolean;
    onClose: () => void;
    customer1: Customer;
    customer2: Customer;
    duplicateCandidate?: DuplicateCandidate;
    onMergeSuccess: (merged: Customer) => void;
}

// Which side is selected for each field: "left" = customer1, "right" = customer2
type FieldSource = Record<MergeableField, 'left' | 'right'>;

function buildDefaultSources(c1: Customer, c2: Customer): FieldSource {
    const result = {} as FieldSource;
    for (const { key } of FIELD_META) {
        const v1 = (c1 as any)[key];
        const v2 = (c2 as any)[key];
        // Default to whichever has a value; if both have, prefer c1 (master side)
        result[key] = v1 ? 'left' : (v2 ? 'right' : 'left');
    }
    return result;
}

function fmt(val: any): string {
    return val ? String(val) : '—';
}

export const CustomerMergeModal: React.FC<Props> = ({
    open, onClose, customer1, customer2, duplicateCandidate, onMergeSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mergeReason, setMergeReason] = useState('');

    // Which customer's ID survives (the "master" account)
    const [masterId, setMasterId] = useState<string>(customer1.customer_id);

    // Per-field selection: "left" = customer1's value, "right" = customer2's value
    const [sources, setSources] = useState<FieldSource>(() => buildDefaultSources(customer1, customer2));

    // Build field_overrides to send to the backend
    const fieldOverrides = useMemo(() => {
        const overrides: Record<string, 'master' | 'duplicate'> = {};
        for (const { key } of FIELD_META) {
            const selectedCustomer = sources[key] === 'left' ? customer1 : customer2;
            overrides[key] = selectedCustomer.customer_id === masterId ? 'master' : 'duplicate';
        }
        return overrides;
    }, [sources, masterId, customer1, customer2]);

    // Live preview of what the merged customer will look like
    const preview = useMemo(() => {
        const obj: Record<string, string> = {};
        for (const { key } of FIELD_META) {
            const src = sources[key] === 'left' ? customer1 : customer2;
            obj[key] = fmt((src as any)[key]);
        }
        return obj;
    }, [sources, customer1, customer2]);

    const handleMerge = async () => {
        setError(null);
        setLoading(true);
        try {
            const merged = await customerMergeService.mergeCustomers({
                master_customer_id: masterId,
                duplicate_customer_id: masterId === customer1.customer_id
                    ? customer2.customer_id
                    : customer1.customer_id,
                merge_reason: mergeReason || undefined,
                field_overrides: fieldOverrides,
            });
            onMergeSuccess(merged);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to merge customers');
        } finally {
            setLoading(false);
        }
    };

    const matchCriteria = duplicateCandidate
        ? customerMergeService.formatMatchCriteria(duplicateCandidate.match_criteria)
        : [];

    // Field picker row: shows both values side-by-side, click to select
    const FieldRow: React.FC<{ meta: typeof FIELD_META[number] }> = ({ meta }) => {
        const v1 = fmt((customer1 as any)[meta.key]);
        const v2 = fmt((customer2 as any)[meta.key]);
        const selected = sources[meta.key];
        const identical = v1 === v2;

        const pick = (side: 'left' | 'right') =>
            setSources(prev => ({ ...prev, [meta.key]: side }));

        return (
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, minHeight: 44 }}>
                {/* Left (customer1) */}
                <Box
                    onClick={() => pick('left')}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer',
                        border: '1.5px solid',
                        borderColor: selected === 'left' ? 'primary.main' : 'divider',
                        bgcolor: selected === 'left' ? 'primary.50' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                    }}
                >
                    {selected === 'left' && <CheckCircle sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />}
                    <Typography variant="body2" sx={{ color: v1 === '—' ? 'text.disabled' : 'text.primary', wordBreak: 'break-word' }}>
                        {v1}
                    </Typography>
                </Box>

                {/* Field label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 110, justifyContent: 'center', flexShrink: 0 }}>
                    {meta.icon}
                    <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                        {meta.label}
                    </Typography>
                    {identical && <Chip label="same" size="small" sx={{ fontSize: 10, height: 18 }} />}
                </Box>

                {/* Right (customer2) */}
                <Box
                    onClick={() => pick('right')}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end',
                        px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer',
                        border: '1.5px solid',
                        borderColor: selected === 'right' ? 'primary.main' : 'divider',
                        bgcolor: selected === 'right' ? 'primary.50' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                    }}
                >
                    <Typography variant="body2" sx={{ color: v2 === '—' ? 'text.disabled' : 'text.primary', wordBreak: 'break-word', textAlign: 'right' }}>
                        {v2}
                    </Typography>
                    {selected === 'right' && <CheckCircle sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />}
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MergeType />
                        <Typography variant="h6">Merge Customers</Typography>
                    </Box>
                    {duplicateCandidate && (
                        <Chip
                            label={`${Math.round(duplicateCandidate.match_score * 100)}% match`}
                            color={duplicateCandidate.match_score >= 0.9 ? 'error' : 'warning'}
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pb: 0 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {matchCriteria.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="caption" fontWeight={600}>Why flagged as duplicate: </Typography>
                        {matchCriteria.join(' · ')}
                    </Alert>
                )}

                {/* Step 1 – Pick master account (whose ID survives) */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    1. Choose which account to keep
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    The kept account's ID and order history remain. The other account will be archived as "merged."
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    {[customer1, customer2].map((c) => (
                        <Paper
                            key={c.customer_id}
                            variant="outlined"
                            onClick={() => setMasterId(c.customer_id)}
                            sx={{
                                flex: 1, p: 1.5, cursor: 'pointer',
                                borderWidth: masterId === c.customer_id ? 2 : 1,
                                borderColor: masterId === c.customer_id ? 'primary.main' : 'divider',
                                bgcolor: masterId === c.customer_id ? 'primary.50' : 'transparent',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" fontWeight={700}>{c.full_name}</Typography>
                                    {c.phone && <Typography variant="caption" color="text.secondary" display="block">{c.phone}</Typography>}
                                    {c.email && <Typography variant="caption" color="text.secondary" display="block">{c.email}</Typography>}
                                </Box>
                                {masterId === c.customer_id && (
                                    <Chip label="Keep" color="primary" size="small" />
                                )}
                            </Box>
                        </Paper>
                    ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Step 2 – Field-by-field picker */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    2. Choose values for the merged customer
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Click a value on either side to select it. The highlighted value will be used.
                </Typography>

                {/* Column headers */}
                <Box sx={{ display: 'flex', mb: 1 }}>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                            {customer1.full_name}
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 110 }} />
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                            {customer2.full_name}
                        </Typography>
                    </Box>
                </Box>

                <Stack spacing={0.75} mb={2}>
                    {FIELD_META.map((meta) => <FieldRow key={meta.key} meta={meta} />)}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {/* Preview */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    3. Merged customer preview
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'success.50' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {FIELD_META.filter(m => preview[m.key] !== '—').map(({ key, label, icon }) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {icon}
                                <Typography variant="caption" color="text.secondary">{label}:</Typography>
                                <Typography variant="caption" fontWeight={600}>{preview[key]}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                    All orders from both customers will be transferred to the kept account. The archived account cannot be reactivated. This cannot be undone.
                </Alert>

                <TextField
                    label="Reason for merging (optional)"
                    value={mergeReason}
                    onChange={(e) => setMergeReason(e.target.value)}
                    fullWidth multiline rows={2}
                    placeholder="e.g. Same person, duplicate registration"
                    sx={{ mb: 1 }}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    onClick={handleMerge}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <MergeType />}
                >
                    {loading ? 'Merging…' : 'Merge Customers'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
